import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Chip, Skeleton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';

/** Lundi de la semaine décalée de `decalage`, à minuit. */
function debutDeSemaine(decalage) {
  const jour = new Date();
  jour.setHours(0, 0, 0, 0);
  // getDay() rend 0 pour dimanche : on ramène la semaine au lundi.
  const recul = (jour.getDay() + 6) % 7;
  jour.setDate(jour.getDate() - recul + decalage * 7);
  return jour;
}

const JOUR = 86400000;
const dateCourte = (d) => d.toLocaleDateString('fr-BE');
const memeJour = (a, b) => a.toDateString() === b.toDateString();

function WeeklyTaskList({ onUpdateStats }) {
  const naviguer = useNavigate();
  const [taches, setTaches] = useState([]);
  const [decalage, setDecalage] = useState(0);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  const lundi = debutDeSemaine(decalage);
  const dimanche = new Date(lundi.getTime() + 6 * JOUR);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      const { data } = await axiosInstance.get('/tasks/',
                                               { params: { status: 'start' } });
      setTaches(data.results || []);
    } catch {
      setErreur("Les tâches n'ont pas pu être chargées.");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const fin = new Date(dimanche.getTime() + JOUR - 1);
  const semaine = taches.filter((t) => {
    if (!t.review_at) return false;
    const d = new Date(t.review_at);
    return d >= lundi && d <= fin;
  });

  // Le compteur du tableau de bord compte la semaine affichée.
  useEffect(() => {
    onUpdateStats?.({ weeklyTasks: semaine.length });
  }, [semaine.length, onUpdateStats]);

  if (chargement && taches.length === 0) {
    return <Stack spacing={1}>{[...Array(4)].map((_, i) => (
      <Skeleton key={i} height={40} />
    ))}</Stack>;
  }

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
             sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          Du {dateCourte(lundi)} au {dateCourte(dimanche)}
          {decalage === 0 && ' — semaine en cours'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<BsChevronLeft />}
                  onClick={() => setDecalage(decalage - 1)}>
            Précédente
          </Button>
          {decalage !== 0 && (
            <Button size="small" variant="outlined"
                    onClick={() => setDecalage(0)}>
              Cette semaine
            </Button>
          )}
          <Button size="small" endIcon={<BsChevronRight />}
                  onClick={() => setDecalage(decalage + 1)}>
            Suivante
          </Button>
        </Stack>
      </Stack>

      {erreur && <Alert severity="error">{erreur}</Alert>}

      {semaine.length === 0 ? (
        <Alert severity="info">Aucune tâche planifiée cette semaine.</Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tâche</TableCell>
                <TableCell>Titulaire</TableCell>
                <TableCell>Échéance</TableCell>
                <TableCell align="right">&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {semaine.map((tache) => {
                const date = new Date(tache.review_at);
                const aujourdhui = memeJour(date, new Date());
                const enRetard = !aujourdhui && date < new Date();
                return (
                  <TableRow
                    key={tache.id}
                    hover
                    sx={{ cursor: tache.expertise ? 'pointer' : 'default',
                          bgcolor: enRetard ? 'warning.light' : undefined }}
                    onClick={() => tache.expertise
                      && naviguer(`/edit-expertise/${tache.expertise}`)}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center"
                             flexWrap="wrap" useFlexGap>
                        <Typography variant="body2">{tache.title}</Typography>
                        {aujourdhui && <Chip size="small" color="primary"
                                             label="Aujourd'hui" />}
                        {enRetard && <Chip size="small" color="warning"
                                           label="En retard" />}
                      </Stack>
                    </TableCell>
                    <TableCell>{tache.receiver || 'Non assignée'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {dateCourte(date)}
                      <Typography variant="caption" color="text.secondary"
                                  component="div">
                        {date.toLocaleDateString('fr-BE', { weekday: 'long' })}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {tache.expertise && (
                        <Button size="small" variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  naviguer(`/edit-expertise/${tache.expertise}`);
                                }}>
                          Ouvrir
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

export default WeeklyTaskList;
